import { usePostProviderRedirectMutation } from "@/queries/auth/mutations";
import { Button } from "@hundred5/design-system";
import { Trans, useLingui } from "@lingui/react/macro";
import Image from "next/image";
import Router from "next/router";
import { ReactNode } from "react";
import {
  AuthenticationError,
  ThirdPartyProvider,
} from "../../types/authentication";
import { Modal } from "../modal";
import * as images from "./images";

interface Props {
  jobSlug?: string;
  testSlug?: string;
  error: AuthenticationError;
}

export function AuthErrorModal(props: Props) {
  const { t } = useLingui();
  const { jobSlug, testSlug, error } = props;
  const providerRedirectMutation = usePostProviderRedirectMutation();

  const thirdPartyLogIn = (
    provider: ThirdPartyProvider,
    jobSlug: string,
    testSlug: string,
  ) => {
    providerRedirectMutation.mutate({
      provider: provider,
      data: { job_opening_slug: jobSlug, test_slug: testSlug },
    });
  };
  const cancel = () => {
    if (jobSlug && testSlug) {
      Router.replace(`/${jobSlug}/${testSlug}`);
    }
  };

  const email = (
    <a href="mailto:support@hire.toggl.com">support@hire.toggl.com</a>
  );

  const thirdPartyProviders: ThirdPartyProvider[] = ["github", "linkedin"];

  const isThirdPartyProvider = (
    provider: string,
  ): provider is ThirdPartyProvider => {
    return thirdPartyProviders.includes(provider as ThirdPartyProvider);
  };

  if (
    error.type === "user_cancelled_login" &&
    error.provider &&
    isThirdPartyProvider(error.provider)
  ) {
    const provider = providerNames[error.provider];

    return (
      <Modal
        open
        color="violet"
        title={t`Canceled authorization`}
        content={
          <>
            <p>
              <Trans>
                To continue, you need to enable the email address permission.
                Please go back to {provider} and log in again.
              </Trans>
            </p>
            {error.traceId ? <p>Trace ID: {error.traceId}</p> : null}
          </>
        }
        buttons={
          <>
            {jobSlug && testSlug && (
              <>
                <LogInButton
                  logIn={() =>
                    thirdPartyLogIn(
                      error.provider as ThirdPartyProvider,
                      jobSlug,
                      testSlug,
                    )
                  }
                >
                  <Trans>Go back to {provider}</Trans>
                </LogInButton>
                <CancelButton onClick={cancel} />
              </>
            )}
          </>
        }
        illustration={<Image src={images.permission} alt="" />}
        onClose={cancel}
      />
    );
  } else {
    return (
      <Modal
        open
        color="yellow"
        title={t`Something went wrong`}
        content={
          <>
            <p>
              <Trans>
                Sorry, something went wrong. Please try again, or contact us at{" "}
                {email} if the problem continues.
              </Trans>
            </p>
            {error.traceId ? <p>Trace ID: {error.traceId}</p> : null}
          </>
        }
        buttons={
          <>
            {jobSlug && testSlug && (
              <>
                {error.provider && isThirdPartyProvider(error.provider) && (
                  <LogInButton
                    logIn={() =>
                      thirdPartyLogIn(
                        error.provider as ThirdPartyProvider,
                        jobSlug,
                        testSlug,
                      )
                    }
                  >
                    <Trans>Try again</Trans>
                  </LogInButton>
                )}
                <CancelButton onClick={cancel} />
              </>
            )}
          </>
        }
        illustration={<Image src={images.error} alt="" />}
        onClose={cancel}
      />
    );
  }
}

function LogInButton(props: { children: ReactNode; logIn: () => void }) {
  return <Button onClick={() => props.logIn()}>{props.children}</Button>;
}

function CancelButton(props: { onClick: () => void }) {
  return (
    <Button variant="secondary" onClick={() => props.onClick()}>
      <Trans comment="Cancel button">Cancel</Trans>
    </Button>
  );
}

const providerNames = {
  facebook: "Facebook",
  linkedin: "LinkedIn",
  github: "GitHub",
};
